import React, { useState, useEffect, useContext } from 'react';
import { FlightSelect, FlightButton } from '@flybits/webapp-design-system-react';
import { MultiStepContext } from 'components/MultiStepDemo/Provider/MultiStepProvider';
import { isButtonContent, getButtonTexts } from 'helpers/multi-demo.helper';
import ContentAPI from 'services/api/content.api';
import { kebabCase, includes } from 'lodash';
import './TriggerEdit.scss';

interface IProps {
  step: any;
  onChange: (e: any) => void;
}

export default function TriggerEdit(props: IProps) {
  const contentAPI = new ContentAPI();
  const { contents, template, isEntryChild }: any = useContext(MultiStepContext);

  const [stepOptions, setStepOptions] = useState<any>([]);
  const [contentOptions, setContentOptions] = useState<any>(null);

  const emptyTrigger = {
    step: null,
    content: null,
    action: null,
  };

  const [triggerData, setTriggerData] = useState<any>([emptyTrigger]);

  // Initialize steps
  useEffect(() => {
    if (template.entry.edges.includes(props.step.id)) {
      setStepOptions([{ key: 'entry-criteria', name: 'Entry Criteria' }]);
    } else {
      // show all other nodes other than the current one
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.step]);

  // Initialize trigger info and content dropdown list options
  useEffect(() => {
    if (props.step?.trigger) {
      setTriggerData(props.step?.trigger);
    }
    const opts = contents.map((content: any) => ({ key: content?.id, name: content?.localizations?.en?.name }));

    //only show previous steps content in trigger.
    const previousContentIds: any[] = [];
    template?.nodes[props.step?.trigger[0]?.step?.key]?.actions.map((item: { type: any; data: any }) => {
      if (item?.type === 'email' || item?.type === 'web-content' || item?.type === 'inapp-content') {
        previousContentIds.push(item?.data?.contentId);
      }
    });

    let filteredContent = opts.filter((x: any) => includes(previousContentIds, x.key));
    setContentOptions(filteredContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notify the actionEdit parent of trigger info
  useEffect(() => {
    props.onChange(triggerData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerData]);

  function addNewTriggerRow() {
    triggerData.push(emptyTrigger);
    setTriggerData([...triggerData]);
  }

  function TriggerRow(params: any) {
    // keep the info internally
    const { trigger } = params;
    const basicActionOptions = [
      { key: 'sent', name: 'Sent' },
      { key: 'view', name: 'View' },
      { key: 'expired', name: 'Expired' },
    ];

    const [actionOptions, setActionOptions] = useState(basicActionOptions);
    const [localTriggerData, setLocalTriggerData] = useState({
      step: trigger.step,
      content: trigger.content,
      action: trigger.action,
    });

    function updateTriggerData(triggerData: any) {
      setLocalTriggerData(triggerData); // update local copy
      params.updateTriggerData(triggerData); // notify parent
    }

    // Update action attributes on content change
    useEffect(() => {
      if (localTriggerData.content) {
        const id = localTriggerData.content.key;
        contentAPI.getInstance(id).then((content: any) => {
          if (isButtonContent(content)) {
            const btns = getButtonTexts(content); // ["Apply Now", "Learn More"]
            const buttonActions = btns.map((name: any) => ({ key: kebabCase(name), name: name }));
            setActionOptions([...basicActionOptions, ...buttonActions]);
          } else {
            setActionOptions([...basicActionOptions]);
          }
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localTriggerData.content]);

    return (
      <div className="flex-container">
        <div className="flex">
          <p> Step </p>
          <FlightSelect
            label=""
            options={stepOptions}
            // first row is disabled for all cards - this is the parent id - auto-generated (locked)
            disabled={params.index === 0}
            selected={localTriggerData.step}
            handleOptionClick={(step: any) => {
              updateTriggerData({ ...localTriggerData, step: step });
            }}
            dropdownMaxHeight="250px"
            width="350px"
          />
        </div>
        <div className="flex">
          <p> Content </p>
          <FlightSelect
            label=""
            options={contentOptions}
            // first row is disabled for immediate child of entry criteria
            disabled={isEntryChild(props.step.id) && params.index === 0}
            selected={localTriggerData.content}
            handleOptionClick={(content: any) => {
              updateTriggerData({ ...localTriggerData, content: content });
            }}
            dropdownMaxHeight="250px"
            width="350px"
          />
        </div>
        <div className="flex">
          <p> Action Attribute </p>
          <FlightSelect
            label=""
            options={actionOptions}
            // first row is disabled for immediate child of entry criteria
            disabled={isEntryChild(props.step.id) && params.index === 0}
            selected={localTriggerData.action}
            handleOptionClick={(action: any) => {
              updateTriggerData({ ...localTriggerData, action: action });
            }}
            dropdownMaxHeight="250px"
            width="350px"
          />
        </div>
        {params.index > 0 && (
          <div className="remove">
            <FlightButton theme="minor" iconLeft="remove" onClick={() => params.removeTriggerData(params.index)} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="trigger-edit">
      <h2> Trigger </h2>
      <p className="description"> Define the trigger for this experience: </p>
      {triggerData.map((trigger: any, index: any) => (
        <TriggerRow
          key={index}
          trigger={trigger}
          index={index}
          removeTriggerData={(index: any) => {
            triggerData.splice(index, 1);
            setTriggerData([...triggerData]);
          }}
          updateTriggerData={(trigger: any) => {
            triggerData[index] = trigger;
            setTriggerData(triggerData);
          }}
        />
      ))}
      {/* Immediate child of entry criteria cannot add additional trigger */}
      {!isEntryChild(props.step.id) && (
        <FlightButton
          theme="secondary"
          iconLeft="add"
          label="Add Another Trigger"
          className="new-trigger"
          onClick={() => addNewTriggerRow()}
        />
      )}
    </div>
  );
}
